// Single source of truth for /tools/combining-assistance-programs.

const SITE = "https://www.homeapproach.com";

export const PAGE_PATH = "/tools/combining-assistance-programs";
export const PAGE_URL = `${SITE}${PAGE_PATH}`;
export const PAGE_TITLE = "Can You Combine Assistance Programs? Stacking Rules Explained";
export const PAGE_DESCRIPTION =
  "Down payment assistance can usually be stacked. Here is what actually disqualifies two assistance programs from being combined: duplicate benefits, income limits, mutual exclusivity, occupancy rules, and lender overlays.";

export const HERO_PROMISE =
  "Most homebuyer assistance is designed to be layered — a state down payment loan on top of a city grant on top of a federal loan program is ordinary, not a loophole. Combinations break for a short list of specific reasons. This page names them so you can check your own stack before you apply.";

export const REASSURANCE =
  "These are the questions applicants ask us most often about combining assistance options. Nothing here says you can only use one program — it explains the handful of rules that stop two programs from working together.";

export const SECTIONS = [
  { id: "two-rulebooks", title: "Two different rulebooks" },
  { id: "checklist", title: "Compatibility checklist" },
  { id: "faq", title: "What disqualifies programs from being combined?" },
  { id: "next", title: "Check your own stack" },
];

export const RULEBOOKS: { id: string; label: string; text: string }[] = [
  {
    id: "public-benefits",
    label: "General public benefits (FEMA, SNAP, SSI, TANF, housing vouchers)",
    text:
      "These run on federal anti-duplication law. The rule is that two agencies cannot pay for the same loss or the same living expense in the same period, and that assistance from one program can count as income or as an asset in another program's eligibility test. Denials here are usually about duplication of benefits or an income and asset ceiling.",
  },
  {
    id: "homebuyer-assistance",
    label: "Homebuyer assistance (DPA, grants, MCCs, affordable first mortgages)",
    text:
      "This is a different system. Layering is normal and often intended — HUD, state housing finance agencies, and cities publish stacking rules and priority orders. What blocks a combination is usually a specific program condition: the same closing cost being covered twice, a stated exclusion, an occupancy or resale restriction, or a lender that will not approve the secondary lien.",
  },
];

export interface ChecklistItem {
  id: string;
  label: string;
  question: string;
}

export const CHECKLIST: ChecklistItem[] = [
  {
    id: "same-expense",
    label: "Same expense",
    question:
      "Are both programs paying the same dollar of the same cost — the same down payment, the same closing fee, the same repair?",
  },
  {
    id: "same-window",
    label: "Same month or closing window",
    question:
      "Do both benefits apply to the same month or the same closing, or does one cover a period the other does not?",
  },
  {
    id: "income",
    label: "Household income and assets",
    question:
      "Does either program count whole-household income, and does the other program's benefit count as income or as an asset that pushes you over the cap?",
  },
  {
    id: "occupancy",
    label: "Occupancy requirement",
    question:
      "Do both programs require the same occupancy status — primary residence, minimum years, no renting out — or do the requirements conflict?",
  },
  {
    id: "funding",
    label: "Funding source conflict",
    question:
      "Do both programs draw on the same funding source (for example two awards from the same HOME or CDBG allocation), which administrators typically cannot double-fund?",
  },
  {
    id: "lender",
    label: "Lender approval",
    question:
      "Has your lender confirmed it will accept both — the subordinate lien position, the gift or grant documentation, and any program-required education certificate?",
  },
];

export type TriggerId = "duplicate" | "limits" | "exclusive" | "overlays";

export const TRIGGER_GROUPS: { id: TriggerId; title: string; blurb: string }[] = [
  {
    id: "duplicate",
    title: "Same expense or time period",
    blurb:
      "Two sources cannot pay the same dollar of the same cost. Split the costs and both usually work.",
  },
  {
    id: "limits",
    title: "Income or asset limits",
    blurb:
      "One benefit can count as income or as an asset in the other program's eligibility test.",
  },
  {
    id: "exclusive",
    title: "Mutual exclusivity",
    blurb:
      "A few programs state in writing that they cannot be paired with a named alternative.",
  },
  {
    id: "overlays",
    title: "Occupancy and lender overlays",
    blurb:
      "Residency and resale conditions can conflict, and a lender can decline a combination the programs allow.",
  },
];

export interface FaqItem {
  id: string;
  trigger: TriggerId;
  q: string;
  a: string;
}

export const FAQS: FaqItem[] = [
  {
    id: "same-cost",
    trigger: "duplicate",
    q: "Can two programs pay for the same cost?",
    a: "Almost never. Assistance programs are written to prevent duplication of benefits: if a grant already covered your $4,000 closing costs, a second program cannot reimburse the same $4,000. What normally works is splitting — one program to the down payment, another to closing costs, a third to prepaids or repairs. Bring a written breakdown of which dollars go where, and most administrators will approve the layering.",
  },
  {
    id: "stack-dpa",
    trigger: "duplicate",
    q: "Can down payment assistance be stacked with another grant?",
    a: "Yes, in most markets. A common stack is a state housing finance agency second mortgage plus a city or county grant plus an employer or nonprofit contribution, all on top of an FHA, VA, USDA, or conventional first mortgage. The stack fails only when two of those cover the same expense, when one names the other as ineligible, or when your lender will not accept the subordinate liens. Ask each administrator for their stacking order in writing before you apply.",
  },
  {
    id: "mcc",
    trigger: "exclusive",
    q: "Does a mortgage credit certificate conflict with down payment assistance?",
    a: "Usually not — MCCs are a tax credit rather than cash at closing, so they sit alongside a DPA loan or grant in most states. Some housing finance agencies do restrict pairing an MCC with a specific bond-financed first mortgage, and a few require you to choose one product. Check the agency's product matrix; the restriction is program-specific, not a general rule.",
  },
  {
    id: "household-income",
    trigger: "limits",
    q: "Do income limits count my income or my whole household's?",
    a: "It varies, and the mismatch is a frequent cause of denial. Some programs count only the borrowers on the loan; others count every adult occupant regardless of who signs. A combination can fail when one program qualifies you on borrower income and the second one counts a spouse, parent, or adult child who lives with you and pushes the file over the area median income cap.",
  },
  {
    id: "assistance-as-income",
    trigger: "limits",
    q: "Can one benefit count as income or as an asset in another program?",
    a: "It can. Cash assistance, forgivable seconds, and matched savings balances are sometimes treated as an asset or as income by a second program's eligibility test — and by needs-based benefits such as SSI. Ask each administrator directly how they treat the other award. Timing the awards in a specific order sometimes solves it.",
  },
  {
    id: "mutual-exclusive",
    trigger: "exclusive",
    q: "Are some programs simply not allowed to be combined?",
    a: "A minority are. The exclusion is stated in the program guidelines — for example a grant that cannot be used with a specific first-mortgage product, or a nonprofit program with terms that only work with its own in-house mortgage. This is the exception rather than the norm, and it is always written down somewhere; ask for the guideline section rather than relying on a verbal answer.",
  },
  {
    id: "same-funder",
    trigger: "exclusive",
    q: "What if two programs come from the same funding source?",
    a: "Administrators generally cannot double-fund one buyer out of the same federal allocation, so two awards that both draw on the same HOME, CDBG, or Housing Trust Fund pot are often mutually exclusive even when the program names are different. Programs funded from different sources — state bonds, city general funds, employer benefits, nonprofit dollars — layer more easily.",
  },
  {
    id: "occupancy",
    trigger: "overlays",
    q: "Do occupancy rules block layering?",
    a: "They can. Most assistance requires the home to be your primary residence, and each program can set its own minimum period, resale restriction, or recapture window. Conflicts show up when one program allows renting out a unit of a duplex and another forbids any rental, or when two different affordability periods create terms you cannot satisfy at the same time. Compare the occupancy conditions side by side before closing.",
  },
  {
    id: "lender-overlays",
    trigger: "overlays",
    q: "Can lender overlays disqualify a combination the programs allow?",
    a: "Yes — this is one of the most common late-stage failures. A lender can refuse a third subordinate lien, cap total assistance, require a higher credit score than the program floor, or decline to work with a particular administrator. The programs are compatible; the lender is not participating. Confirm with the loan officer early, and if the answer is no, ask each program for its list of approved lenders.",
  },
  {
    id: "education",
    trigger: "overlays",
    q: "Does my homebuyer education certificate count for every program?",
    a: "Often, but not always. Many programs accept any HUD-approved course certificate, while some require their own curriculum or an in-person session with a local counselor. If two programs require different courses, you may need both certificates. Check the accepted providers before you pay for a class.",
  },
  {
    id: "funding-runs-out",
    trigger: "duplicate",
    q: "What happens if one program runs out of funding mid-application?",
    a: "Funding rounds close, sometimes with little notice, and a stack built around a program that closed has to be rebuilt. That is a timing problem rather than a compatibility problem. Keep a backup layer identified, ask each administrator about the current funding window and waitlist, and re-run your options if a closing date slips.",
  },
];

export const COMPLIANCE_NOTE =
  "Compatibility varies by program administrator, state, lender, and funding source, and program rules change between funding rounds. Nothing here is a determination of your eligibility or a commitment to lend. Verify the exact rules with each program administrator and your lender in writing before you apply.";

export const DISCLAIMER =
  "Educational information only, not legal, tax, or lending advice. Home Approach is not a lender and does not administer assistance programs.";

export const COMBINING_ASSISTANCE_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
